import { useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { hasSessionHint } from "../lib/auth";

/**
 * Shared chrome for every authenticated dashboard page. Auth gate is here:
 * if the session-hint cookie is missing, redirect to /login.
 *
 * The actual session token is in an HttpOnly cookie set by the Worker — we
 * can't read it from JS. The hint cookie is set as a sibling for client-side
 * presence-check only.
 */
export function DashboardLayout() {
	const nav = useNavigate();
	const loc = useLocation();

	useEffect(() => {
		if (!hasSessionHint() && loc.pathname !== "/login") {
			nav("/login", { replace: true, state: { from: loc.pathname } });
		}
	}, [loc.pathname, nav]);

	return (
		<div style={layoutStyles.root}>
			<aside style={layoutStyles.sidebar}>
				<div style={layoutStyles.brand}>INSIGHTS</div>
				<nav style={layoutStyles.nav}>
					<NavItem to="/">Home Brief</NavItem>
					<NavItem to="/placements">Placements</NavItem>
					<NavItem to="/configurations">Configurations</NavItem>
					<NavItem to="/substitution">Substitution</NavItem>
					<NavItem to="/funnel">Funnel</NavItem>
					<NavSection>Pro</NavSection>
					<NavItem to="/alerts">Alerts</NavItem>
					<NavItem to="/exports">Exports</NavItem>
					<NavSection>Account</NavSection>
					<NavItem to="/settings">Settings</NavItem>
				</nav>
				<footer style={layoutStyles.footer}>
					<div>Cohort minimum k=5 (k=10 cross-tab and substitution).</div>
					<div>All numbers based on opt-in agents only.</div>
					<div>
						<a href="https://aivision.space/trust" style={layoutStyles.link} target="_blank" rel="noopener">
							Read the Trust Charter
						</a>
					</div>
				</footer>
			</aside>
			<main style={layoutStyles.main}>
				<Outlet />
			</main>
		</div>
	);
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
	return (
		<NavLink
			to={to}
			end={to === "/"}
			style={({ isActive }) => ({
				...layoutStyles.navItem,
				...(isActive ? layoutStyles.navItemActive : {}),
			})}
		>
			{children}
		</NavLink>
	);
}

function NavSection({ children }: { children: React.ReactNode }) {
	return <div style={layoutStyles.navSection}>{children}</div>;
}

const layoutStyles: Record<string, React.CSSProperties> = {
	root: { display: "grid", gridTemplateColumns: "240px 1fr", minHeight: "100vh" },
	sidebar: {
		background: "var(--card-bg)",
		borderRight: "1px solid var(--border)",
		padding: "24px 16px",
		display: "flex",
		flexDirection: "column",
		gap: 12,
	},
	brand: { fontWeight: 600, fontSize: 18, letterSpacing: -0.3, marginBottom: 16 },
	nav: { display: "flex", flexDirection: "column", gap: 2, flex: 1 },
	navItem: {
		padding: "8px 10px",
		borderRadius: 6,
		color: "var(--fg)",
		textDecoration: "none",
		fontSize: 14,
	},
	navItemActive: { background: "var(--accent)", color: "white" },
	navSection: {
		fontSize: 11,
		textTransform: "uppercase",
		letterSpacing: 0.5,
		color: "var(--muted)",
		marginTop: 16,
		marginBottom: 4,
		padding: "0 10px",
	},
	footer: {
		fontSize: 11,
		color: "var(--muted)",
		borderTop: "1px solid var(--border)",
		paddingTop: 12,
		display: "flex",
		flexDirection: "column",
		gap: 4,
	},
	link: { color: "var(--accent)" },
	main: { padding: 32, overflow: "auto" },
};
